/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { useSortable } from '@dnd-kit/sortable'
import React from 'react'
import colors from '@sportninja/common/constants/appColors'

interface SidebarItemContentProps {
  icon?: React.ReactNode
  children: React.ReactNode
  isDragOverlay?: boolean
  disabled?: boolean
  dragListeners?: React.DOMAttributes<HTMLDivElement>
}

/**
 * Reusable content component for sidebar items - used both in sortable items and DragOverlay
 */
export const SidebarItemContent: React.FC<SidebarItemContentProps> = ({
  icon,
  children,
  isDragOverlay = false,
  disabled = false,
  dragListeners,
}) => {
  return (
    <div
      css={css`
        position: relative;
        display: flex;
        align-items: center;
        width: 100%;
        ${isDragOverlay
          ? `
          background-color: ${colors.SECONDARY_400};
          border-radius: 8px;
          padding: 8px 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
          cursor: grabbing;
        `
          : ''}
        .drag-handle {
          opacity: ${isDragOverlay ? 1 : 0};
          transition: opacity 0.2s;
        }
        &:hover .drag-handle {
          opacity: 1;
        }
      `}
    >
      {icon && (
        <span
          css={css`
            margin-right: 10px;
            display: flex;
            align-items: center;
            font-size: 16px;
          `}
        >
          {icon}
        </span>
      )}
      <div
        css={css`
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: inherit;
        `}
      >
        {children}
      </div>
      {!disabled && (
        <div
          className='drag-handle'
          {...dragListeners}
          css={css`
            position: absolute;
            right: -10px;
            top: 50%;
            transform: translateY(-50%);
            cursor: ${isDragOverlay ? 'grabbing' : 'grab'};
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 4px;
            z-index: 10;
            &:active {
              cursor: grabbing;
            }
          `}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='16'
            height='16'
            viewBox='0 0 16 16'
            fill='none'
          >
            <path
              d='M13.3333 6H2.66663V7.33333H13.3333V6ZM2.66663 10H13.3333V8.66667H2.66663V10Z'
              fill='#C1C2C3'
            />
          </svg>
        </div>
      )}
    </div>
  )
}

interface SortableSidebarItemProps {
  id: string
  children: React.ReactNode
  icon?: React.ReactNode
  disabled?: boolean
}

const SortableSidebarItem: React.FC<SortableSidebarItemProps> = ({
  id,
  children,
  icon,
  disabled = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled })

  // Use Translate instead of Transform for better compatibility with nested structures
  // Only apply Y translation to keep items within their horizontal bounds
  const style: React.CSSProperties = {
    transform: transform ? `translateY(${transform.y}px)` : undefined,
    transition,
    opacity: isDragging ? 0 : 1,
    position: 'relative',
    zIndex: transform ? 1 : 0,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      css={css`
        cursor: default;
      `}
    >
      <SidebarItemContent
        icon={icon}
        disabled={disabled}
        dragListeners={disabled ? undefined : listeners}
      >
        {children}
      </SidebarItemContent>
    </div>
  )
}

export default SortableSidebarItem
